// ProductsAddPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiUrl } from "../config/api";
import { useNavigate } from "react-router-dom";
import ProductsPicker, { ProductLite } from "../components/ProductsPicker";

type NewProductForm = {
  product_code: string;
  product_name: string;
  retail_price: number | "";
  wholesale_price: number | "";
  mall_price: number | ""; 
  stock_on_hand: number;
  unit: string;
};
type GroupRow = { id: number; group_name: string };



type Alert = { show: boolean; message: string; type?: "success" | "warning" | "info" | "danger" };

// ---------- Image resize helpers ----------
type ResizeOpts = {
  maxWidth?: number;
  maxHeight?: number;
  mimeType?: "image/jpeg" | "image/webp" | "image/png";
  quality?: number;        // 0..1 (jpeg/webp เท่านั้น)
  targetMaxBytes?: number; // บังคับไม่ให้เกินขนาดไฟล์ เช่น 1.5MB
};

async function fileToImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    await new Promise<void>((resolve, reject) => {
      const probe = new Image();
      probe.onload = () => resolve();
      probe.onerror = reject;
      probe.src = url;
    });
    const img = new Image();
    img.src = url;
    return img;
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob_failed"))), type, quality);
  });
}

/** ย่อรูปตามสัดส่วน + บีบอัดคุณภาพจนต่ำกว่า targetMaxBytes (ถ้ากำหนด) */
async function resizeImage(
  file: File,
  opts: ResizeOpts = {}
): Promise<{ blob: Blob; file: File; previewUrl: string }> {
  const {
    maxWidth = 1280,
    maxHeight = 1280,
    mimeType = "image/jpeg",
    quality = 0.85,
    targetMaxBytes, // เช่น 1.5 * 1024 * 1024
  } = opts;

  // แปลง HEIC/HEIF -> JPEG เพื่อความเข้ากันได้
  const outType = /heic|heif/i.test(file.type) ? "image/jpeg" : mimeType;

  const img = await fileToImage(file);


  // คำนวณสเกล (ไม่ขยายรูป)
  let { width, height } = img;
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, width, height);

  let q = quality;
  let blob = await canvasToBlob(canvas, outType, q);

  if (targetMaxBytes && blob.size > targetMaxBytes) {
    while (q > 0.5 && blob.size > targetMaxBytes) {
      q -= 0.07;
      blob = await canvasToBlob(canvas, outType, q);
    }
  }

  const newName = file.name.replace(/\.(heic|heif|webp|png|jpg|jpeg)$/i, "") + (outType === "image/webp" ? ".webp" : ".jpg");
  const resizedFile = new File([blob], newName, { type: outType });
  const previewUrl = URL.createObjectURL(blob);

  return { blob, file: resizedFile, previewUrl };
}

// =============================================================

export default function ProductsAddPage(): JSX.Element {
  const navigate = useNavigate();
  const productCodeRef = useRef<HTMLInputElement>(null);

  // ----- UI State -----
  const [form, setForm] = useState<NewProductForm>({
    product_code: "",
    product_name: "",
    retail_price: "",
    wholesale_price: "",
    mall_price: "", 
    stock_on_hand: 0,
    unit: "",
  });

  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupId, setGroupId] = useState<string>(""); // เก็บ id เป็นสตริง หรือ "" = ไม่ระบุ

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  // toast แบบเดียวกับหน้าอื่นๆ
  const [toast, setToast] = useState<Alert>({ show: false, message: "", type: "success" });

  const [showPicker, setShowPicker] = useState(false)

  // ---------- Upload image states ----------
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [submittingUpload, setSubmittingUpload] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ type: "success" | "warning" | "danger" | "" | "info"; msg: string }>({
    type: "",
    msg: "",
  });
  const [productCreated, setProductCreated] = useState(false); // true หลังบันทึกสินค้าสำเร็จอย่างน้อยหนึ่งครั้ง

  const canUpload = useMemo(
    () => !!form.product_code.trim() && !!file && !submittingUpload,
    [form.product_code, file, submittingUpload]
  );

  // ---------- Effects ----------
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2200);
    return () => clearTimeout(t);
  }, [toast.show]);

  useEffect(() => {
    (async () => {
      try {
        setLoadingGroups(true);
        const resp = await fetch(apiUrl("/groups_list"));
        const j = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(j?.error ?? resp.statusText);
  
        const items: GroupRow[] = Array.isArray(j?.items) ? j.items : [];
        // เรียง id จากมาก → น้อย
        items.sort((a, b) => b.id - a.id);
  
        setGroups(items);
      } catch (e) {
        console.warn("load groups failed:", e);
        setGroups([]);
      } finally {
        setLoadingGroups(false);
      }
    })();
  }, []);

  // ---------- Helpers ----------
  const update = (patch: Partial<NewProductForm>) => setForm((f) => ({ ...f, ...patch }));

  const resetForm = () => {
    setForm({
      product_code: "",
      product_name: "",
      retail_price: "",
      wholesale_price: "",
      mall_price: "", 
      stock_on_hand: 0,
      unit: "",
    });
    setProductCreated(false);
    setFile(null);
    setPreview("");
    setUploadStatus({ type: "", msg: "" });

    // โฟกัสกลับช่องรหัสสินค้า
    setTimeout(() => productCodeRef.current?.focus(), 0);
  };

  const validate = (): string | null => {
    if (!form.product_code.trim()) return "กรุณากรอกรหัสสินค้า";
    if (!form.product_name.trim()) return "กรุณากรอกชื่อสินค้า";
    if (form.retail_price === "" || Number(form.retail_price) < 0)
      return "ราคาปลีกต้องเป็นเลข 0 ขึ้นไป";
    if (form.wholesale_price === "" || Number(form.wholesale_price) < 0)
      return "ราคาส่งต้องเป็นเลข 0 ขึ้นไป";
    if (form.mall_price === "" || Number(form.mall_price) < 0)
      return "ราคาขายบนห้างต้องเป็นเลข 0 ขึ้นไป";
    return null;
  };

  // ---------- Pick/Resize image ----------
  const onPick = async (f: File | null) => {
    setUploadStatus({ type: "", msg: "" });

    if (!f) {
      setFile(null);
      setPreview("");
      return;
    }

    try {
      const { file: resized, previewUrl } = await resizeImage(f, {
        maxWidth: 1280,
        maxHeight: 1280,
        mimeType: "image/jpeg",
        quality: 0.85,
        targetMaxBytes: 1.5 * 1024 * 1024, // 1.5MB
      });
      setFile(resized);
      setPreview(previewUrl);
    } catch (err) {
      // ถ้าย่อไม่สำเร็จ ใช้ไฟล์เดิม
      setFile(f);
      setPreview(URL.createObjectURL(f));
      setUploadStatus({ type: "warning", msg: "ย่อรูปไม่สำเร็จ ใช้ไฟล์เดิมแทน" });
    }
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] ?? null;
    void onPick(f);
  };

  // ---------- Submit ----------
  const submit = async () => {
    setErrorMsg("");
    const invalid = validate();
    if (invalid) {
      setErrorMsg(invalid);
      setToast({ show: true, message: invalid, type: "warning" });
      return;
    }
    if (saving) return;
    setSaving(true);

    // PMN-CRITICAL(/ProductsAddPage): product master starts with stock_on_hand=0; real sellable stock must be created later via StockInPage inventory_movements.
    const payload = {
      product_code: form.product_code.trim(),
      product_name: form.product_name.trim(),
      retail_price: Number(form.retail_price || 0),
      wholesale_price: Number(form.wholesale_price || 0),
      mall_price: Number(form.mall_price || 0),  // <---
      stock_on_hand: 0,
      unit: form.unit.trim() || null,
      groups_type_id: groupId.trim() === "" ? null : Number(groupId),
    };

    // ยิง 1 ครั้ง (มี timeout + mapping error)
    const postOnce = async () => {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 15000);
      try {
        const resp = await fetch(apiUrl("/productsAdd"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: ctrl.signal,
        });

        const ct = resp.headers.get("content-type") || "";
        const raw = await resp.text();
        let data: any = raw;
        if (ct.includes("application/json")) {
          try { data = JSON.parse(raw); } catch {}
        }

        if (!resp.ok) {
          const serverErr =
            (typeof data === "object" && (data?.error || data?.message)) ||
            (typeof data === "string" ? data : "");

          // 409/400 → แจ้งตรง ไม่รีทราย
          if (resp.status === 409 && serverErr === "product_code_already_exists") {
            throw new Error(`รหัสสินค้า "${payload.product_code}" ถูกใช้แล้ว`);
          }
          if (resp.status >= 400 && resp.status < 500) {
            throw new Error(serverErr || resp.statusText || "คำขอไม่ถูกต้อง");
          }

          // 500 → บอกให้ลองใหม่
          if (resp.status === 500) {
            throw new Error("เซิร์ฟเวอร์ขัดข้อง ลองใหม่อีกครั้ง");
          }

          // 5xx อื่น ๆ → ให้เข้าเงื่อนไขรีทราย
          throw new Error(serverErr || `HTTP ${resp.status}`);
        }

        return data;
      } finally {
        clearTimeout(timer);
      }
    };

    const maxRetries = 2;
    let attempt = 0;

    try {
      while (true) {
        try {
          await postOnce();
          break;
        } catch (err: any) {
          const msg = String(err?.message || "");
          const retryable =
            err?.name === "AbortError" ||               // timeout
            msg === "เซิร์ฟเวอร์ขัดข้อง ลองใหม่อีกครั้ง" || // map 500
            err instanceof TypeError ||                 // เครือข่ายล้ม
            /^HTTP 5\d{2}/.test(msg);                   // 5xx อื่นๆ

          if (!retryable || attempt >= maxRetries) {
            throw err;
          }
          const backoff = 400 * Math.pow(2, attempt);   // 400ms, 800ms
          await new Promise(r => setTimeout(r, backoff));
          attempt++;
        }
      }

      setToast({ show: true, message: `เพิ่มสินค้า “${payload.product_name}” สำเร็จ`, type: "success" });

      // กันระบบตามทันเล็กน้อย
      await new Promise(r => setTimeout(r, 150));
      if (file) await uploadImage();
      await new Promise(r => setTimeout(r, 100));

      // ล้างฟอร์ม + โฟกัสช่องรหัส
      resetForm();

      // (ทางเลือก) กลับหน้าเมนูอัตโนมัติหลังบันทึกสำเร็จ
      // setTimeout(() => navigate("/"), 600);
    } catch (err: any) {
      const friendly =
        err?.name === "AbortError"
          ? "เครือข่ายช้าหรือหมดเวลา (timeout)"
          : err instanceof TypeError
          ? "โหลดไม่สำเร็จ (เช็กสัญญาณอินเทอร์เน็ต)"
          : err?.message || "เชื่อมต่อ API ไม่ได้ — ตรวจสอบเซิร์ฟเวอร์/เน็ตเวิร์ค";
      setErrorMsg(friendly);
      setToast({ show: true, message: friendly, type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  // ---------- อัปโหลดรูปไป /upload/:code ----------
  const uploadImage = async () => {
    // PMN-CRITICAL(/ProductsAddPage): image upload is a second side effect after product create; failure must not imply the product row was not created.
    if (!canUpload) return;
    try {
      setSubmittingUpload(true);
      setUploadStatus({ type: "info" as any, msg: "กำลังอัปโหลด..." });

      const code = form.product_code.trim();
      const fd = new FormData();
      fd.append("file", file!);

      const endpoint = apiUrl(`/upload/${encodeURIComponent(code)}`);
      const res = await fetch(endpoint, { method: "POST", body: fd });

      const ct = res.headers.get("content-type") || "";
      const payload: any = ct.includes("application/json") ? await res.json() : await res.text();

      if (!res.ok || (typeof payload === "object" && payload?.ok === false)) {
        const msg = (typeof payload === "object" ? payload?.error : String(payload)) || "upload_failed";
        throw new Error(msg);
      }

      const url = (typeof payload === "object" ? payload?.url : undefined) as string | undefined;
      setUploadStatus({ type: "success", msg: `อัปโหลดสำเร็จ! ${url ? `URL: ${url}` : ""}` });
    } catch (err: any) {
      const emsg = String(err?.message || "upload_failed");
      const friendly =
        emsg === "product_not_found"
          ? "ยังไม่พบรหัสสินค้านี้ในระบบ กรุณากด “บันทึกสินค้าใหม่” ก่อนอัปโหลดรูป"
          : emsg;
      setUploadStatus({ type: "danger", msg: `ล้มเหลว: ${friendly}` });
    } finally {
      setSubmittingUpload(false);
    }
  };

  // เดารหัสถัดไปจากโค้ดล่าสุด เช่น "PD009" -> "PD010"
  function nextCode(raw: string | null | undefined) {
    if (!raw) return "";
    const m = String(raw).trim().match(/^([A-Za-z]+)(\d+)$/);
    if (!m) return ""; // ถ้ารูปแบบไม่ใช่ PREFIX+ตัวเลข ไม่เดา
    const [, prefix, numStr] = m;
    const nextNum = (parseInt(numStr, 10) || 0) + 1;
    return prefix + String(nextNum).padStart(numStr.length, "0");
  }

  const [latestCode, setLatestCode] = useState<string>("");
  const [nextSuggestion, setNextSuggestion] = useState<string>("");

  const fetchLatestCode = async () => {
    try {
      const r = await fetch(apiUrl("/products/latest_code"));
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error ?? r.statusText);
      const code = j?.code ? String(j.code) : "";
      setLatestCode(code);
      setNextSuggestion(nextCode(code));
    } catch (e) {
      console.warn("fetchLatestCode failed:", e);
      setLatestCode("");
      setNextSuggestion("");
    }
  };


  // ---------- UI ----------
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast มุมขวาบน — สอดคล้องทุกหน้า */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}>{toast.message}</div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <h4 className="mb-3 text-primary text-center">เพิ่มสินค้าใหม่ (Products)</h4>

                <div className="border rounded-3 p-3 bg-white mb-3">
                  {/* รหัสสินค้า */}
                  <div className="mb-3">
                    <div className="d-flex align-items-start justify-content-between">
                      <label htmlFor="product_code" className="form-label mb-0">รหัสสินค้า</label>

                      {/* ฝั่งขวา: เรียงเป็นคอลัมน์ */}
                      <div className="d-flex flex-column align-items-end gap-2">
                        {/* แถวบน: ป้าย + ใส่รหัสถัดไป + รีเฟรช (เรียงแนวนอน) */}
                        <div className="d-flex align-items-center gap-2">
                          {latestCode && (
                            <span className="badge text-bg-light">ล่าสุด: <strong>{latestCode}</strong></span>
                          )}
                          {nextSuggestion && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              title="ใส่รหัสถัดไปอัตโนมัติ"
                              onClick={() => update({ product_code: nextSuggestion })}
                            >
                              ใส่ {nextSuggestion}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-link btn-sm"
                            onClick={() => void fetchLatestCode()}
                            title="รีเฟรชรหัสล่าสุด"
                          >
                            รีเฟรช
                          </button>
                        </div>

                        {/* แถวล่าง: ปุ่มดูทั้งหมด (อยู่ใต้รีเฟรช) */}
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                          onClick={() => setShowPicker(true)}
                          title="เปิดหน้าต่างรายการสินค้าทั้งหมด"
                        >
                          ดูทั้งหมด
                        </button>
                      </div>
                    </div>

                    {/* ช่องกรอกรหัส (อันเดียวพอ) */}
                    <input
                      id="product_code"
                      ref={productCodeRef}
                      className="form-control"
                      placeholder="เช่น dc025 / ABC123"
                      value={form.product_code}
                      onChange={(e) => update({ product_code: e.target.value })}
                    />
                    <div className="form-text">ต้องไม่ซ้ำในระบบ</div>
                  </div>

                  {/* Modal เลือกสินค้า */}
                  <ProductsPicker
                    isOpen={showPicker}
                    onClose={() => setShowPicker(false)}
                    onPick={(p: ProductLite) => {
                      update({ product_code: p.code });
                      setShowPicker(false);
                      setTimeout(() => document.getElementById("product_name")?.focus(), 0);
                    }}
                  />


                  {/* ชื่อสินค้า */}
                  <div className="mb-3">
                    <label htmlFor="product_name" className="form-label">ชื่อสินค้า</label>
                    <input
                      id="product_name"
                      className="form-control"
                      placeholder="เช่น ดินสอกด 0.5 มม."
                      value={form.product_name}
                      onChange={(e) => update({ product_name: e.target.value })}
                    />
                  </div>

                  {/* กลุ่มสินค้า + ราคาปลีก */}
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center justify-content-between">
                        <label className="form-label mb-0 me-2 text-nowrap">กลุ่มสินค้า</label>
                        <a
                          href="/AddGroupPage"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                          title="เพิ่มกลุ่มสินค้าใหม่"
                        >
                          + เพิ่มกลุ่มสินค้าใหม่
                        </a>
                      </div>
                      <select
                        className="form-select mt-2"
                        value={groupId}
                        onChange={(e) => setGroupId(e.target.value)}
                        disabled={loadingGroups}
                      >
                        <option value="">— ไม่ระบุ —</option>
                        {groups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.group_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor="retail_price" className="form-label">ราคาปลีก (บาท)</label>
                      <input
                        id="retail_price"
                        type="number"
                        min={0}
                        step="0.01"
                        className="form-control"
                        value={form.retail_price}
                        onChange={(e) =>
                          update({
                            retail_price: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)),
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* ราคาส่ง + ราคาขายบนห้าง */}
                  <div className="row g-3 mt-0">
                    <div className="col-12 col-md-6">
                      <label htmlFor="wholesale_price" className="form-label">ราคาส่ง (บาท)</label>
                      <input
                        id="wholesale_price"
                        type="number"
                        min={0}
                        step="0.01"
                        className="form-control"
                        value={form.wholesale_price}
                        onChange={(e) =>
                          update({
                            wholesale_price: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)),
                          })
                        }
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor="mall_price" className="form-label">ราคาขายบนห้าง (บาท)</label>
                      <input
                        id="mall_price"
                        type="number"
                        min={0}
                        step="0.01"
                        className="form-control"
                        value={form.mall_price}
                        onChange={(e) =>
                          update({
                            mall_price: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)),
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* หน่วย & สต็อกตั้งต้น (ย้ายมาอีกแถว) */}
                  <div className="row g-3 mt-0">
                    <div className="col-12 col-md-6">
                      <label htmlFor="unit" className="form-label">หน่วย</label>
                      <input
                        id="unit"
                        className="form-control"
                        list="units"
                        placeholder="เช่น ชิ้น / กล่อง / เล่ม"
                        value={form.unit}
                        onChange={(e) => update({ unit: e.target.value })}
                      />
                      <datalist id="units">
                        <option value="ชิ้น" />
                        <option value="กล่อง" />
                        <option value="เล่ม" />
                        <option value="แพ็ค" />
                      </datalist>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">สต็อกตั้งต้น</label>
                      <input className="form-control" value={0} readOnly />
                      <div className="form-text">ระบบตั้งค่าเริ่มต้นเป็น 0 เสมอ</div>
                    </div>
                  </div>
                </div>

                {/* ---------- ส่วนอัปโหลดรูป ---------- */}
                <div className="border rounded-3 p-3 bg-white mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="form-label mb-0">อัปโหลดรูปสินค้า (ออปชัน)</label>
                    {file && <span className="badge text-bg-secondary">{file.name}</span>}
                  </div>

                  <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="w-100 d-flex align-items-center justify-content-center bg-light"
                    style={{
                      border: "2px dashed #c9ccd1",
                      borderRadius: 12,
                      padding: 20,
                      cursor: "pointer",
                      minHeight: 120,
                    }}
                    onClick={() => document.getElementById("file-input")?.click()}
                    title="ลากไฟล์มาวาง หรือคลิกเพื่อเลือกไฟล์"
                  >
                    <input
                      id="file-input"
                      type="file"
                      accept="image/*"
                      capture="environment" /* เปิดกล้องหลังสะดวกบนมือถือ */
                      onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
                      style={{ display: "none" }}
                    />
                    {preview ? (
                      <img
                        src={preview}
                        alt="preview"
                        className="img-fluid shadow-sm"
                        style={{ maxHeight: 220, borderRadius: 10, objectFit: "contain", background: "#fff" }}
                      />
                    ) : (
                      <div className="text-muted">ลากรูปมาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์</div>
                    )}
                  </div>
                  <div className="form-text">รองรับไฟล์ภาพทั่วไป (jpeg, png, webp ฯลฯ) — ระบบจะย่อรูปให้อัตโนมัติ</div>

                  {uploadStatus.msg && (
                    <div className={`alert alert-${uploadStatus.type || "secondary"} rounded-3 mt-3`} role="alert">
                      {uploadStatus.msg}
                    </div>
                  )}

                  <div className="d-grid mt-3">
                    <button
                      className="btn btn-outline-primary"
                      disabled={!canUpload}
                      onClick={() => {
                        if (!productCreated) {
                          setUploadStatus({
                            type: "warning",
                            msg: "กรุณากด “บันทึกสินค้าใหม่” ให้สำเร็จก่อน แล้วค่อยอัปโหลดรูปนะ",
                          });
                          return;
                        }
                        void uploadImage();
                      }}
                    >
                      {submittingUpload ? "กำลังอัปโหลด…" : "📤 อัปโหลดรูปสำหรับรหัสนี้"}
                    </button>
                  </div>
                </div>

                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    <div className="fw-semibold">ข้อผิดพลาด:</div>
                    <div className="text-break">{errorMsg}</div>
                  </div>
                )}

                {/* ปุ่มบันทึก */}
                <div className="d-grid">
                  <button type="button" className="btn btn-primary btn-lg" disabled={saving} onClick={submit}>
                    {saving ? "กำลังบันทึก…" : "💾 บันทึกสินค้าใหม่"}
                  </button>
                </div>

                {/* ปุ่มล้าง/ย้อนกลับ */}
                <div className="d-flex gap-2 justify-content-between mt-3">
                  <button type="button" className="btn btn-outline-secondary flex-fill" onClick={resetForm}>
                    ล้างฟอร์ม
                  </button>
                  <a href="/" className="btn btn-link text-decoration-none flex-fill text-center">
                    ← กลับหน้าเมนู
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * หน้านี้ใช้สไตล์การ์ด/สี/ปุ่มเดียวกับหน้า Scan และ Stock In เพื่อให้สอดคล้องกัน
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
